import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import type { Recipe } from "@/types/menu";

export interface FavoriteItem {
  recipe_slug: string;
  kind: "meal" | "batch";
  created_at: string;
}

interface ToggleResponse {
  active: boolean;
  recipe_slug: string;
  kind: "meal" | "batch";
}

interface UseFavoritesReturn {
  favorites: FavoriteItem[];
  toggle: (
    slug: string,
    kind?: "meal" | "batch",
    recipe?: Recipe,
  ) => Promise<void>;
  isFavorite: (slug: string) => boolean;
  isLoading: boolean;
}

/**
 * Hook for managing recipe favorites (Phase 12).
 *
 * Fetches favorites on mount, provides optimistic toggle, and exposes
 * a simple `isFavorite` check.
 */
export function useFavorites(): UseFavoritesReturn {
  const [favorites, setFavorites] = useState<FavoriteItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [optimisticSet, setOptimisticSet] = useState<Set<string>>(new Set());

  // One-time fetch on mount.
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    api<FavoriteItem[]>("/api/v1/favorites")
      .then((data) => {
        if (!cancelled) {
          setFavorites(data);
          setOptimisticSet(new Set(data.map((f) => f.recipe_slug)));
        }
      })
      .catch(() => {
        // Silently fail — favourites are best-effort.
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isFavorite = useCallback(
    (slug: string) => optimisticSet.has(slug),
    [optimisticSet],
  );

  const toggle = useCallback(
    async (slug: string, kind: "meal" | "batch" = "meal", recipe?: Recipe) => {
      // Optimistic toggle.
      const was = optimisticSet.has(slug);
      const newSet = new Set(optimisticSet);
      if (was) newSet.delete(slug);
      else newSet.add(slug);
      setOptimisticSet(newSet);

      try {
        const body: Record<string, unknown> = { recipe_slug: slug, kind };
        if (recipe) body.recipe = recipe;
        const res = await api<ToggleResponse>("/api/v1/favorites/toggle", {
          method: "POST",
          body,
        });
        // Sync with server result.
        setOptimisticSet((prev) => {
          const next = new Set(prev);
          if (res.active) next.add(res.recipe_slug);
          else next.delete(res.recipe_slug);
          return next;
        });
        // Refresh the underlying list.
        const updated = await api<FavoriteItem[]>("/api/v1/favorites");
        setFavorites(updated);
      } catch {
        // Revert optimistic update on error.
        setOptimisticSet(new Set(favorites.map((f) => f.recipe_slug)));
      }
    },
    [optimisticSet, favorites],
  );

  return { favorites, toggle, isFavorite, isLoading };
}
